import type { OrderItem } from '@/types'

/**
 * F-FE-N154 — the one place that decides what a check SHOWS and what it CHARGES.
 *
 * These were two hand-written filters that drifted apart. The checkout panel
 * billed `payOrder.items.filter(i => i.status !== 'voided')`, which includes
 * unfired `draft` lines; the rail's "On order" section filtered `draft` out and
 * then returned null on an empty list. So an order whose KOT fire never landed
 * rendered a payable bill with NO line list at all — the cashier could charge
 * Rs 3,780 they could not see, void, or remove.
 *
 * Parched order 20260910-040 sat like that on a live table mid-service, and
 * four earlier orders were charged and completed the same way. See
 * audit/PARCHED_UNFIRED_ORDER_NO_VOID_BANNER_2026-09-10.md.
 *
 * The fix is a shared predicate rather than a contract test over two filters:
 * a test tells you they drifted, this makes drifting impossible. Anything the
 * bill charges, the rail lists.
 */
export function isBillableLine(item: Pick<OrderItem, 'status'>): boolean {
  return item.status !== 'voided'
}

/**
 * Unsent = the kitchen has no record of this line yet.
 *
 * `draft` is where every dine-in line STARTS — createOrderCore inserts dine-in
 * items as 'draft' and non-dine-in as 'pending' (orders.go), and the KOT fire is
 * what promotes them (kot.go). So an unsent line is not an error state in
 * itself; it is an error state only when it outlives the fire that should have
 * cleared it.
 *
 * Crucially, unsent does NOT mean "the food was never made" — it means our
 * record of the fire request didn't land. The kitchen may well have it, verbally
 * or from a slip that printed before the call failed. That is exactly why an
 * unsent line stays billable: the till must never refuse a sale on the strength
 * of an inference about what the kitchen received. Show it, let staff fire or
 * remove it, never block the money.
 *
 * Mirrors RemoveDraftItem's guard in backend/internal/handlers/kot.go:1741,
 * which accepts exactly these two statuses — so the two must agree or the UI
 * offers a remove the server refuses.
 */
export const UNSENT_STATUSES: ReadonlySet<OrderItem['status']> = new Set([
  'draft',
  'pending',
] as const)

export function isUnsentLine(item: Pick<OrderItem, 'status'>): boolean {
  return UNSENT_STATUSES.has(item.status)
}
